<script setup>
import { ref, computed, watch, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { debounce } from '@chatwoot/utils';
import ContactAPI from 'dashboard/api/contacts';

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'create']);

const { t } = useI18n();

// Form data
const selectedContactId = ref(null);
const selectedContact = ref(null);
const selectedInboxId = ref(null);
const customMessage = ref('');
const sendDay = ref(5);
const sendTime = ref('09:00');
const respectBusinessHours = ref(true);

// Contact search
const contactSearch = ref('');
const searchResults = ref([]);
const isSearching = ref(false);
const showDropdown = ref(false);

// Ref for textarea
const messageTextarea = useTemplateRef('messageTextarea');

// Message variables (only those we have data for)
const MESSAGE_VARIABLES = [
  { variable: '{nome}', label: 'OMNI.BOLETOS.VARIABLES.NAME', icon: 'i-lucide-user' },
  { variable: '{mes_referencia}', label: 'OMNI.BOLETOS.VARIABLES.REFERENCE_MONTH', icon: 'i-lucide-calendar' },
  { variable: '{dia_envio}', label: 'OMNI.BOLETOS.VARIABLES.SEND_DAY', icon: 'i-lucide-calendar-clock' },
];

// Data from store
const inboxes = useMapGetter('inboxes/getInboxes');

// Filtered inboxes (only those that support attachments)
const supportedInboxes = computed(() => {
  return inboxes.value.filter(inbox => {
    const type = inbox.channel_type;
    return ['Channel::Whatsapp', 'Channel::TwilioSms', 'Channel::Sms', 'Channel::Api', 'Channel::Email'].includes(type);
  });
});

// Get selected inbox object
const selectedInbox = computed(() => {
  if (!selectedInboxId.value) return null;
  return inboxes.value.find(inbox => inbox.id === selectedInboxId.value);
});

// Check if selected inbox has working hours enabled
const inboxHasWorkingHours = computed(() => {
  return selectedInbox.value?.working_hours_enabled || false;
});

// Get working days from inbox (days that are not closed_all_day)
const workingDaysOfWeek = computed(() => {
  if (!selectedInbox.value?.working_hours) return [0, 1, 2, 3, 4, 5, 6];
  return selectedInbox.value.working_hours
    .filter(wh => !wh.closed_all_day)
    .map(wh => wh.day_of_week);
});

// Helper to check if a day of month falls on a working day
const isWorkingDay = (dayOfMonth) => {
  if (!inboxHasWorkingHours.value || !respectBusinessHours.value) return true;
  
  // Build a reference date for current month
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const lastDay = new Date(year, month + 1, 0).getDate();
  const actualDay = Math.min(dayOfMonth, lastDay);
  const date = new Date(year, month, actualDay);
  const dayOfWeek = date.getDay();
  
  return workingDaysOfWeek.value.includes(dayOfWeek);
};

// Find nearest working day (looking backwards first, then forwards)
const findNearestWorkingDay = (dayOfMonth) => {
  if (isWorkingDay(dayOfMonth)) return dayOfMonth;
  
  // Try days before
  for (let offset = 1; offset <= 7; offset++) {
    let newDay = dayOfMonth - offset;
    if (newDay <= 0) newDay = 28 + newDay;
    if (isWorkingDay(newDay)) return newDay;
  }
  
  // Try days after
  for (let offset = 1; offset <= 7; offset++) {
    let newDay = dayOfMonth + offset;
    if (newDay > 28) newDay = newDay - 28;
    if (isWorkingDay(newDay)) return newDay;
  }
  
  return dayOfMonth;
};

// Calculate effective send days considering business hours
const effectiveSendDays = computed(() => {
  // For add modal, we only have 1 send (send_count is set in ConfigModal)
  const baseDays = [sendDay.value];
  
  if (!inboxHasWorkingHours.value || !respectBusinessHours.value) {
    return baseDays;
  }
  
  return baseDays.map(day => findNearestWorkingDay(day));
});

// Check if send day was adjusted due to business hours
const sendDayAdjusted = computed(() => {
  if (!inboxHasWorkingHours.value || !respectBusinessHours.value) return false;
  return effectiveSendDays.value[0] !== sendDay.value;
});

// Debounced contact search
const searchContacts = debounce(async query => {
  if (!query || query.length < 2) {
    searchResults.value = [];
    showDropdown.value = false;
    return;
  }
  
  isSearching.value = true;
  showDropdown.value = true;
  try {
    const { data: { payload } } = await ContactAPI.search(query);
    searchResults.value = payload || [];
  } catch (error) {
    searchResults.value = [];
    console.error('Error searching contacts:', error);
  } finally {
    isSearching.value = false;
  }
}, 300);

// Handle search input
const handleSearchInput = event => {
  const query = event.target.value;
  contactSearch.value = query;
  searchContacts(query);
};

// Select contact
const selectContact = contact => {
  selectedContactId.value = contact.id;
  selectedContact.value = contact;
  contactSearch.value = contact.name;
  showDropdown.value = false;
  searchResults.value = [];
};

// Clear contact selection
const clearContact = () => {
  selectedContactId.value = null;
  selectedContact.value = null;
  contactSearch.value = '';
  searchResults.value = [];
};

// Insert variable into message
const insertVariable = variable => {
  const textarea = messageTextarea.value;
  if (!textarea) {
    customMessage.value = (customMessage.value || '') + variable;
    return;
  }
  
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const currentValue = customMessage.value || '';
  
  customMessage.value = 
    currentValue.substring(0, start) + 
    variable + 
    currentValue.substring(end);
  
  const newCursorPos = start + variable.length;
  setTimeout(() => {
    textarea.focus();
    textarea.setSelectionRange(newCursorPos, newCursorPos);
  }, 0);
};

const isValid = computed(() => {
  return selectedContactId.value && selectedInboxId.value && sendDay.value && sendTime.value;
});

// Reset form when modal closes
watch(
  () => props.isOpen,
  newVal => {
    if (!newVal) {
      selectedContactId.value = null;
      selectedContact.value = null;
      selectedInboxId.value = null;
      customMessage.value = '';
      sendDay.value = 5;
      sendTime.value = '09:00';
      respectBusinessHours.value = true;
      contactSearch.value = '';
      searchResults.value = [];
      showDropdown.value = false;
    }
  }
);

const handleSubmit = () => {
  if (!isValid.value) return;

  emit('create', {
    contact_id: selectedContactId.value,
    inbox_id: selectedInboxId.value,
    custom_message: customMessage.value || null,
    send_day: sendDay.value,
    send_time: sendTime.value,
    respect_business_hours: respectBusinessHours.value,
  });
};

const dayOptions = Array.from({ length: 28 }, (_, i) => i + 1);
</script>

<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
    @click.self="emit('close')"
  >
    <div
      class="bg-n-solid-2 rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-hidden flex flex-col"
    >
      <!-- Header -->
      <div
        class="flex items-center justify-between p-4 border-b border-n-weak"
      >
        <h3 class="text-lg font-semibold text-n-slate-12">
          {{ t('OMNI.BOLETOS.ADD_CLIENT_MODAL.TITLE') }}
        </h3>
        <button
          class="p-1 text-n-slate-10 hover:text-n-slate-12"
          @click="emit('close')"
        >
          <span class="i-lucide-x text-xl" />
        </button>
      </div>

      <!-- Content -->
      <div class="p-4 overflow-y-auto flex-1 space-y-4">
        <!-- Contact Search -->
        <div class="relative">
          <label
            class="block text-sm font-medium text-n-slate-12 mb-1"
          >
            {{ t('OMNI.BOLETOS.ADD_CLIENT_MODAL.CONTACT') }}
          </label>
          
          <!-- Selected contact display -->
          <div v-if="selectedContact" class="flex items-center gap-2 p-2 bg-woot-50 dark:bg-woot-900/30 rounded-lg border border-woot-200 dark:border-woot-700">
            <div class="w-8 h-8 rounded-full bg-woot-500 flex items-center justify-center text-white text-sm font-medium">
              {{ selectedContact.name?.charAt(0).toUpperCase() || '?' }}
            </div>
            <div class="flex-1 min-w-0">
              <div class="font-medium text-n-slate-12 truncate">{{ selectedContact.name }}</div>
              <div class="text-xs text-n-slate-11 truncate">{{ selectedContact.phone_number || selectedContact.email }}</div>
            </div>
            <button type="button" class="p-1 text-n-slate-10 hover:text-red-500" @click="clearContact">
              <span class="i-lucide-x" />
            </button>
          </div>
          
          <!-- Search input -->
          <div v-else class="relative">
            <input
              v-model="contactSearch"
              type="text"
              :placeholder="t('OMNI.BOLETOS.ADD_CLIENT_MODAL.SEARCH_CONTACT')"
              class="w-full px-2 py-1.5 text-xs border border-n-weak rounded-lg bg-n-alpha-3 text-n-slate-12 placeholder:text-n-slate-10 focus:ring-2 focus:ring-woot-500 focus:border-transparent transition-shadow"
              @input="handleSearchInput"
              @focus="contactSearch.length >= 2 && (showDropdown = true)"
            />
            <span v-if="isSearching" class="absolute right-3 top-1/2 -translate-y-1/2 i-lucide-loader-2 animate-spin text-n-slate-10 size-4" />
          </div>
          
          <!-- Dropdown results -->
          <div
            v-if="showDropdown && (searchResults.length > 0 || isSearching)"
            class="absolute z-10 w-full mt-1 bg-n-solid-3 border border-n-weak rounded-lg shadow-lg max-h-48 overflow-y-auto"
          >
            <div v-if="isSearching" class="p-3 text-center text-n-slate-11">
              <span class="i-lucide-loader-2 animate-spin mr-2" />
              {{ t('OMNI.COMMON.LOADING') }}
            </div>
            <div v-else-if="searchResults.length === 0" class="p-3 text-center text-n-slate-11">
              {{ t('OMNI.BOLETOS.ADD_CLIENT_MODAL.NO_CONTACTS') }}
            </div>
            <div
              v-for="contact in searchResults"
              v-else
              :key="contact.id"
              class="flex items-center gap-3 p-2 hover:bg-n-alpha-3 cursor-pointer"
              @click="selectContact(contact)"
            >
              <div
                class="w-8 h-8 rounded-full bg-n-alpha-3 flex items-center justify-center text-sm font-medium text-n-slate-12"
              >
                {{ contact.name?.charAt(0).toUpperCase() || '?' }}
              </div>
              <div class="flex-1 min-w-0">
                <div class="font-medium text-n-slate-12 truncate">
                  {{ contact.name }}
                </div>
                <div class="text-xs text-n-slate-11 truncate">
                  {{ contact.phone_number || contact.email }}
                </div>
              </div>
            </div>
          </div>
          
          <p v-if="!selectedContact" class="mt-1 text-xs text-n-slate-11">
            {{ t('OMNI.BOLETOS.ADD_CLIENT_MODAL.SEARCH_HINT') }}
          </p>
        </div>

        <!-- Inbox Selection -->
        <div>
          <label
            class="block text-sm font-medium text-n-slate-12 mb-1"
          >
            {{ t('OMNI.BOLETOS.ADD_CLIENT_MODAL.INBOX') }}
          </label>
          <select
            v-model="selectedInboxId"
            class="w-full px-3 py-2 border border-n-weak rounded-lg bg-n-alpha-3 text-n-slate-12"
          >
            <option :value="null" disabled>
              {{ t('OMNI.BOLETOS.ADD_CLIENT_MODAL.SELECT_INBOX') }}
            </option>
            <option
              v-for="inbox in supportedInboxes"
              :key="inbox.id"
              :value="inbox.id"
            >
              {{ inbox.name }}
            </option>
          </select>
          <p class="mt-1 text-xs text-n-slate-11">
            {{ t('OMNI.BOLETOS.ADD_CLIENT_MODAL.INBOX_HINT') }}
          </p>
        </div>

        <!-- Send Schedule -->
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label
              class="block text-sm font-medium text-n-slate-12 mb-1"
            >
              {{ t('OMNI.BOLETOS.ADD_CLIENT_MODAL.SEND_DAY') }}
            </label>
            <select
              v-model="sendDay"
              class="w-full px-3 py-2 border border-n-weak rounded-lg bg-n-alpha-3 text-n-slate-12"
            >
              <option v-for="day in dayOptions" :key="day" :value="day">
                {{ t('OMNI.BOLETOS.ADD_CLIENT_MODAL.DAY') }} {{ day }}
              </option>
            </select>
          </div>
          <div>
            <label
              class="block text-sm font-medium text-n-slate-12 mb-1"
            >
              {{ t('OMNI.BOLETOS.ADD_CLIENT_MODAL.SEND_TIME') }}
            </label>
            <input
              v-model="sendTime"
              type="time"
              class="w-full px-3 py-2 border border-n-weak rounded-lg bg-n-alpha-3 text-n-slate-12"
            />
          </div>
        </div>

        <!-- Business Hours Option -->
        <div class="mt-4">
          <label class="flex items-center gap-2 cursor-pointer">
            <input
              v-model="respectBusinessHours"
              type="checkbox"
              class="w-4 h-4 rounded border-n-weak text-woot-500 focus:ring-woot-500"
            />
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('OMNI.BOLETOS.ADD_CLIENT_MODAL.RESPECT_BUSINESS_HOURS') }}
            </span>
          </label>
          <p class="mt-1 text-xs text-n-slate-11 ml-6">
            {{ t('OMNI.BOLETOS.ADD_CLIENT_MODAL.RESPECT_BUSINESS_HOURS_HINT') }}
          </p>

          <!-- Show adjusted send day info (only when inbox has working hours configured) -->
          <div
            v-if="inboxHasWorkingHours && respectBusinessHours && sendDayAdjusted"
            class="mt-2 p-2 bg-amber-50 dark:bg-amber-900/30 rounded text-xs text-amber-700 dark:text-amber-300"
          >
            <span class="i-lucide-alert-triangle mr-1" />
            {{ t('OMNI.BOLETOS.ADD_CLIENT_MODAL.SEND_DAY_ADJUSTED', { original: sendDay, effective: effectiveSendDays[0] }) }}
          </div>

          <!-- Info when inbox doesn't have working hours -->
          <div
            v-if="selectedInboxId && !inboxHasWorkingHours && respectBusinessHours"
            class="mt-2 p-2 bg-blue-50 dark:bg-blue-900/30 rounded text-xs text-blue-700 dark:text-blue-300"
          >
            <span class="i-lucide-info mr-1" />
            {{ t('OMNI.BOLETOS.ADD_CLIENT_MODAL.NO_WORKING_HOURS_INFO') }}
          </div>
        </div>

        <!-- Custom Message with Variables -->
        <div>
          <label
            class="block text-sm font-medium text-n-slate-12 mb-1"
          >
            {{ t('OMNI.BOLETOS.ADD_CLIENT_MODAL.MESSAGE') }}
          </label>
          <textarea
            ref="messageTextarea"
            v-model="customMessage"
            rows="3"
            :placeholder="t('OMNI.BOLETOS.ADD_CLIENT_MODAL.MESSAGE_PLACEHOLDER')"
            class="w-full px-3 py-2 border border-n-weak rounded-lg bg-n-alpha-3 text-n-slate-12 placeholder:text-n-slate-10 resize-none"
          />
          
          <!-- Variable buttons -->
          <div class="mt-2">
            <p class="text-xs text-n-slate-11 mb-2">
              {{ t('OMNI.BOLETOS.ADD_CLIENT_MODAL.INSERT_VARIABLES') }}
            </p>
            <div class="flex flex-wrap gap-1.5">
              <button
                v-for="varItem in MESSAGE_VARIABLES"
                :key="varItem.variable"
                type="button"
                class="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md
                       bg-n-alpha-3 hover:bg-n-slate-4 
                       text-n-slate-11 hover:text-n-slate-12
                       border border-n-weak transition-all"
                @click="insertVariable(varItem.variable)"
              >
                <span :class="varItem.icon" class="size-3" />
                {{ t(varItem.label) }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div
        class="flex items-center justify-end gap-2 p-4 border-t border-n-weak"
      >
        <button
          class="px-4 py-2 text-sm font-medium text-n-slate-12 bg-n-alpha-3 rounded-lg hover:bg-n-slate-4"
          @click="emit('close')"
        >
          {{ t('OMNI.BOLETOS.CANCEL') }}
        </button>
        <button
          class="px-4 py-2 text-sm font-medium text-white bg-woot-500 rounded-lg hover:bg-woot-600 disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="!isValid"
          @click="handleSubmit"
        >
          {{ t('OMNI.BOLETOS.ADD_CLIENT_MODAL.SUBMIT') }}
        </button>
      </div>
    </div>
  </div>
</template>
